import { MD5, enc, lib } from 'crypto-js';
import type { AuthTokens, TokenPayload } from '@/types/auth';

/**
 * Convierte un string a un array de bytes
 * Cada carácter se convierte en dos bytes: uno para el código menos significativo y otro para el más significativo
 * @param str String a convertir
 * @returns Array de bytes
 */
function stringToByteArray(str: string): number[] {
  let bytes: number[] = [];
  for (let i = 0; i < str.length; ++i) {
    const code = str.charCodeAt(i);
    bytes = bytes.concat([code & 0xff, (code / 256) >>> 0]);
  }
  return bytes;
}

/**
 * Convierte un array a una cadena hexadecimal
 * @param arr Array de bytes
 * @returns String en formato hexadecimal
 */
function toHexString(arr: number[]): string {
  const arrayreturn = arr
    .map((byte) => (`0${(byte & 0xff).toString(16)}`).slice(-2))
    .join('-');
  return arrayreturn.toUpperCase();
}

/**
 * Convierte un WordArray de CryptoJS a un array de bytes
 * @param wordArray El WordArray de CryptoJS
 * @returns Array de bytes
 */
function wordArrayToByteArray(wordArray: any): number[] {
  const words = wordArray.words;
  const sigBytes = wordArray.sigBytes;
  const bytes = [];
  
  for (let i = 0; i < sigBytes; i++) {
    const byte = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
    bytes.push(byte);
  }
  
  return bytes;
}

/**
 * Hashea una contraseña siguiendo el mismo algoritmo que el cliente existente
 * @param password Contraseña en texto plano
 * @returns Contraseña hasheada
 */
export function hashPassword(password: string): string {
  try {
    // Convertir string a array de bytes
    const byteArray = stringToByteArray(password);
    
    // Aplicar MD5 al array de bytes
    const wordArray = lib.WordArray.create(new Uint8Array(byteArray));
    const hash = MD5(wordArray);
    
    // Convertir resultado a array de bytes y luego a string hexadecimal
    const hashBytes = wordArrayToByteArray(hash);
    const hexString = toHexString(hashBytes);
    
    // Aplicar MD5 al string hexadecimal
    return MD5(hexString).toString();
  } catch (error) {
    console.error('Error hashing password:', error);
    return MD5(password).toString(); // Fallback
  }
}

export function hashPasswordSignup(password: string): string {
  try {
    // Convertir string a array de bytes
    const byteArray = stringToByteArray(password);
    
    // Aplicar MD5 al array de bytes
    const wordArray = lib.WordArray.create(new Uint8Array(byteArray));
    const hash = MD5(wordArray);
    
    // Convertir resultado a array de bytes y luego a string hexadecimal
    const hashBytes = wordArrayToByteArray(hash);
    const hexString = toHexString(hashBytes);
    
    return hexString;
  } catch (error) {
    console.error('Error hashing password:', error);
  }
}

/**
 * Almacena los tokens de autenticación en el localStorage Y en una cookie.
 * La cookie (rts_auth_token) es necesaria para que los Server Components de Next.js
 * puedan leer el token y reenviarlo al backend en llamadas SSR (ej: GetEventByAlias).
 * @param tokens Tokens de autenticación
 */
export function storeTokens(tokens: AuthTokens): void {
  if (typeof window === 'undefined') return;
  
  localStorage.setItem('auth_tokens', JSON.stringify(tokens));
  localStorage.setItem('auth_access_token', tokens.accessToken);
  localStorage.setItem('auth_refresh_token', tokens.refreshToken);

  // Calcular Max-Age en segundos a partir de la fecha de expiración del token
  const maxAge = tokens.expiresAt
    ? Math.max(0, Math.floor((new Date(tokens.expiresAt).getTime() - Date.now()) / 1000))
    : 86400; // 24h por defecto si no hay fecha de expiración

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `rts_auth_token=${tokens.accessToken}; path=/; SameSite=Lax; Max-Age=${maxAge}${secure}`;
}

/**
 * Recupera los tokens de autenticación del localStorage
 * @returns Tokens de autenticación o null si no hay tokens
 */
export function getStoredTokens(): AuthTokens | null {
  if (typeof window === 'undefined') return null;
  
  const tokensJson = localStorage.getItem('auth_tokens');
  if (!tokensJson) return null;
  
  try {
    return JSON.parse(tokensJson) as AuthTokens;
  } catch (e) {
    console.error('Error parsing stored tokens:', e);
    return null;
  }
}

/**
 * Borra los tokens de autenticación del localStorage y de la cookie
 */
export function clearTokens(): void {
  if (typeof window === 'undefined') return;
  
  localStorage.removeItem('auth_tokens');
  localStorage.removeItem('auth_access_token');
  localStorage.removeItem('auth_refresh_token');

  // Eliminar también la cookie de autenticación (Max-Age=0 la borra inmediatamente)
  document.cookie = 'rts_auth_token=; path=/; SameSite=Lax; Max-Age=0';
}

/**
 * Verifica si el usuario está autenticado basado en la existencia de tokens
 * @returns true si hay tokens almacenados y no han expirado
 */
export function isAuthenticated(): boolean {
  try {
    // En el servidor, siempre devolver false
    if (typeof window === 'undefined') {
      return false;
    }
    
    // Obtener tokens del localStorage
    const tokensJson = localStorage.getItem('auth_tokens');
    
    if (!tokensJson) {
      return false;
    }

    try {
      // Parsear tokens
      const tokens = JSON.parse(tokensJson) as AuthTokens;
      
      if (!tokens.accessToken) {
        return false;
      }

      // Verificar expiración si hay fecha de expiración
      if (tokens.expiresAt) {
        const expiresAt = new Date(tokens.expiresAt).getTime();
        const now = new Date().getTime();
        
        const isExpired = now >= expiresAt;
        
        if (isExpired) {
          return false;
        }
      }
      
      return true;
    } catch (error) {
      return false;
    }
  } catch (error) {
    console.error('[auth lib] Error in isAuthenticated:', error);
    return false;
  }
}